'use client';

import { useState, useMemo } from 'react';
import { Search, Plus, Eye, Edit2, Receipt, Calendar, AlertTriangle, Trash2 } from 'lucide-react';
import { DataTable } from '../../../components/shared/DataTable';
import { PageHeader } from '../../../components/shared/PageHeader';
import { Modal, FormField, FormInput, FormSelect, FormTextarea, Button } from '../../../components/shared/Modal';
import { purchaseInvoices, suppliers, purchaseOrders } from '../data/mock-data';
import { formatCurrency } from '../../../types';
import { purchaseInvoiceStatusConfig, type PurchaseInvoiceStatus } from '../types';
import type { PurchaseInvoice } from '../types';

// 청구서 등록 모달
function PurchaseInvoiceAddModal({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  const [items, setItems] = useState([{ itemName: '', quantity: 1, unitPrice: 0 }]);

  const addItem = () => setItems([...items, { itemName: '', quantity: 1, unitPrice: 0 }]);
  const removeItem = (index: number) => {
    if (items.length > 1) setItems(items.filter((_, i) => i !== index));
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="청구서 등록"
      description="새로운 구매 청구서를 등록합니다."
      size="xl"
      footer={
        <>
          <Button variant="outline" onClick={onClose}>취소</Button>
          <Button onClick={onClose}>등록</Button>
        </>
      }
    >
      <div className="space-y-6">
        <div className="grid grid-cols-2 gap-4">
          <FormField label="청구번호" required>
            <FormInput placeholder="자동 생성" disabled />
          </FormField>
          <FormField label="발주서 참조">
            <FormSelect>
              <option value="">선택 안함</option>
              {purchaseOrders.map(po => (
                <option key={po.id} value={po.id}>{po.code} - {po.supplierName}</option>
              ))}
            </FormSelect>
          </FormField>
          <FormField label="공급업체" required>
            <FormSelect>
              <option value="">선택하세요</option>
              {suppliers.filter(s => s.isActive).map((supplier) => (
                <option key={supplier.id} value={supplier.id}>{supplier.name}</option>
              ))}
            </FormSelect>
          </FormField>
          <FormField label="청구일" required>
            <FormInput type="date" />
          </FormField>
          <FormField label="마감일" required>
            <FormInput type="date" />
          </FormField>
          <FormField label="공급업체 청구번호">
            <FormInput placeholder="공급업체 세금계산서 번호" />
          </FormField>
        </div>

        <div>
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-medium">청구 품목</h3>
            <Button variant="outline" size="sm" onClick={addItem}>
              <Plus className="w-4 h-4 mr-1" />품목 추가
            </Button>
          </div>
          <div className="border border-border rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted/50">
                <tr>
                  <th className="text-left p-3 font-medium">품목</th>
                  <th className="text-right p-3 font-medium w-24">수량</th>
                  <th className="text-right p-3 font-medium w-32">단가</th>
                  <th className="text-right p-3 font-medium w-32">금액</th>
                  <th className="w-12"></th>
                </tr>
              </thead>
              <tbody>
                {items.map((_, index) => (
                  <tr key={index} className="border-t border-border">
                    <td className="p-2"><FormInput placeholder="품목명" /></td>
                    <td className="p-2"><FormInput type="number" min={1} defaultValue={1} className="text-right" /></td>
                    <td className="p-2"><FormInput type="number" min={0} defaultValue={0} className="text-right" /></td>
                    <td className="p-2 text-right font-medium">₩0</td>
                    <td className="p-2">
                      <button onClick={() => removeItem(index)} className="p-1 rounded hover:bg-accent" disabled={items.length === 1}>
                        <Trash2 className="w-4 h-4 text-muted-foreground" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="flex justify-end">
          <div className="w-64 space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-muted-foreground">소계</span><span>₩0</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">부가세 (10%)</span><span>₩0</span></div>
            <div className="flex justify-between pt-2 border-t border-border font-bold text-base"><span>청구 금액</span><span>₩0</span></div>
          </div>
        </div>

        <FormField label="비고">
          <FormTextarea rows={2} placeholder="청구서 관련 메모를 입력하세요" />
        </FormField>
      </div>
    </Modal>
  );
}

export function PurchaseInvoiceListPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  const [selectedSupplier, setSelectedSupplier] = useState<string>('all');
  const [isModalOpen, setIsModalOpen] = useState(false);

  const filteredData = useMemo(() => {
    return purchaseInvoices.filter((invoice) => {
      // 검색 필터
      const matchesSearch =
        invoice.code.toLowerCase().includes(searchQuery.toLowerCase()) ||
        invoice.supplierName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (invoice.purchaseOrderCode && invoice.purchaseOrderCode.toLowerCase().includes(searchQuery.toLowerCase()));

      // 상태 필터
      const matchesStatus =
        selectedStatus === 'all' || invoice.status === selectedStatus;

      // 공급업체 필터
      const matchesSupplier =
        selectedSupplier === 'all' || invoice.supplierId === selectedSupplier;

      return matchesSearch && matchesStatus && matchesSupplier;
    });
  }, [searchQuery, selectedStatus, selectedSupplier]);

  const columns = [
    {
      key: 'code',
      label: '청구번호',
      width: '12%',
      sortable: true,
      render: (value: unknown, row: PurchaseInvoice) => (
        <div>
          <span className="font-mono text-sm text-primary">{String(value)}</span>
          {row.status === 'overdue' && (
            <div className="flex items-center gap-1 text-xs text-red-500 mt-0.5">
              <AlertTriangle className="w-3 h-3" />
              <span>연체</span>
            </div>
          )}
        </div>
      ),
    },
    {
      key: 'supplierName',
      label: '공급업체',
      width: '18%',
      sortable: true,
      render: (value: unknown, row: PurchaseInvoice) => (
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center flex-shrink-0">
            <Receipt className="w-4 h-4 text-purple-600 dark:text-purple-400" />
          </div>
          <div className="min-w-0">
            <div className="font-medium truncate">{String(value)}</div>
            {row.purchaseOrderCode && (
              <div className="text-xs text-muted-foreground truncate">
                발주: {row.purchaseOrderCode}
              </div>
            )}
          </div>
        </div>
      ),
    },
    {
      key: 'invoiceDate',
      label: '청구일',
      width: '12%',
      sortable: true,
      render: (value: unknown, row: PurchaseInvoice) => (
        <div>
          <div className="flex items-center gap-1 text-sm">
            <Calendar className="w-3 h-3 text-muted-foreground flex-shrink-0" />
            <span className="whitespace-nowrap">{String(value)}</span>
          </div>
          <div className={`text-xs whitespace-nowrap ${row.status === 'overdue' ? 'text-red-500' : 'text-muted-foreground'}`}>
            마감: {row.dueDate}
          </div>
        </div>
      ),
    },
    {
      key: 'grandTotal',
      label: '청구금액',
      width: '14%',
      align: 'right' as const,
      sortable: true,
      render: (value: unknown) => (
        <span className="font-medium whitespace-nowrap">{formatCurrency(value as number)}</span>
      ),
    },
    {
      key: 'paidAmount',
      label: '지급액',
      width: '14%',
      align: 'right' as const,
      render: (value: unknown, row: PurchaseInvoice) => (
        <div>
          <div className="text-sm whitespace-nowrap">{formatCurrency(value as number)}</div>
          {row.outstandingAmount > 0 && (
            <div className="text-xs text-red-500 whitespace-nowrap">
              잔액: {formatCurrency(row.outstandingAmount)}
            </div>
          )}
        </div>
      ),
    },
    {
      key: 'status',
      label: '상태',
      width: '8%',
      render: (value: unknown) => {
        const status = value as PurchaseInvoiceStatus;
        const config = purchaseInvoiceStatusConfig[status];
        const colorClasses: Record<string, string> = {
          gray: 'bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-400',
          blue: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
          yellow: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
          orange: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',
          green: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
          red: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
        };
        return (
          <span className={`px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${colorClasses[config.color]}`}>
            {config.label}
          </span>
        );
      },
    },
    {
      key: 'actions',
      label: '',
      width: '8%',
      render: () => (
        <div className="flex items-center gap-1">
          <button className="p-1.5 rounded hover:bg-accent" title="보기">
            <Eye className="w-4 h-4 text-muted-foreground" />
          </button>
          <button className="p-1.5 rounded hover:bg-accent" title="수정">
            <Edit2 className="w-4 h-4 text-muted-foreground" />
          </button>
        </div>
      ),
    },
  ];

  // 상태별 통계
  const statusStats = useMemo(() => {
    const stats: Record<string, number> = {};
    purchaseInvoices.forEach((invoice) => {
      stats[invoice.status] = (stats[invoice.status] || 0) + 1;
    });
    return stats;
  }, []);

  // 요약 정보
  const totalInvoices = purchaseInvoices.length;
  const totalAmount = purchaseInvoices.reduce((sum, i) => sum + i.grandTotal, 0);
  const totalOutstanding = purchaseInvoices.reduce((sum, i) => sum + i.outstandingAmount, 0);
  const overdueCount = purchaseInvoices.filter((i) => i.status === 'overdue').length;

  return (
    <div className="space-y-6">
      <PageHeader
        title="구매 청구서 관리"
        description="구매 청구서를 조회하고 지급을 관리합니다."
        actions={
          <button
            onClick={() => setIsModalOpen(true)}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90"
          >
            <Plus className="w-4 h-4" />
            청구서 등록
          </button>
        }
      />

      {/* 요약 카드 */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="p-4 rounded-lg bg-card border border-border">
          <div className="text-sm text-muted-foreground">전체 청구서</div>
          <div className="text-2xl font-bold mt-1">{totalInvoices}건</div>
        </div>
        <div className="p-4 rounded-lg bg-card border border-border">
          <div className="text-sm text-muted-foreground">총 청구금액</div>
          <div className="text-2xl font-bold mt-1">{formatCurrency(totalAmount)}</div>
        </div>
        <div className="p-4 rounded-lg bg-card border border-border">
          <div className="text-sm text-muted-foreground">미지급 잔액</div>
          <div className="text-2xl font-bold mt-1 text-red-500">
            {formatCurrency(totalOutstanding)}
          </div>
        </div>
        <div className="p-4 rounded-lg bg-card border border-border">
          <div className="text-sm text-muted-foreground">연체 청구서</div>
          <div className="text-2xl font-bold mt-1 text-red-500">
            {overdueCount}건
          </div>
        </div>
      </div>

      {/* 상태별 필터 버튼 */}
      <div className="flex flex-wrap gap-2">
        <button
          onClick={() => setSelectedStatus('all')}
          className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
            selectedStatus === 'all'
              ? 'bg-primary text-primary-foreground'
              : 'bg-card border border-border hover:bg-accent'
          }`}
        >
          전체 ({totalInvoices})
        </button>
        {Object.entries(purchaseInvoiceStatusConfig).map(([status, config]) => (
          <button
            key={status}
            onClick={() => setSelectedStatus(status)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
              selectedStatus === status
                ? 'bg-primary text-primary-foreground'
                : 'bg-card border border-border hover:bg-accent'
            }`}
          >
            {config.label} ({statusStats[status] || 0})
          </button>
        ))}
      </div>

      {/* 필터 영역 */}
      <div className="flex flex-wrap gap-4">
        {/* 검색 */}
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="청구번호, 발주번호, 공급업체 검색..."
            className="w-full h-10 pl-10 pr-4 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>

        {/* 공급업체 필터 */}
        <select
          value={selectedSupplier}
          onChange={(e) => setSelectedSupplier(e.target.value)}
          className="h-10 px-3 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary"
        >
          <option value="all">전체 공급업체</option>
          {suppliers
            .filter((s) => s.isActive)
            .map((supplier) => (
              <option key={supplier.id} value={supplier.id}>
                {supplier.name}
              </option>
            ))}
        </select>
      </div>

      {/* 데이터 테이블 */}
      <DataTable
        data={filteredData}
        columns={columns}
        pageSize={10}
        emptyMessage="검색 결과가 없습니다."
      />

      {/* 청구서 등록 모달 */}
      <PurchaseInvoiceAddModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </div>
  );
}
